/**
 * Phase 8 T-06c §21.E — Collections picker dropdown.
 *
 * Opens above the emerald `Save →` action when a thread has no attached
 * collection. Two-section dropdown:
 *   - inline create input (revealed by `[+ Create new]`)
 *   - scrollable list of existing collections
 *
 * Selecting a collection invokes `onPick({ id, name })` — the parent is
 * expected to attach it to thread state (which in turn flips the emerald
 * label from `Save` to `Shortlist` per §21.B / `shortlistLabelFor`).
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { FolderOpen, Plus } from 'lucide-react';
import {
  COLLECTIONS_CHANGE_EVENT,
  createCollection,
  readCollections,
  type SmartMatchCollection,
} from '../lib/smartMatchCollections';
import type { SmartMatchCollectionRef } from '../lib/smartMatchThreads';

interface CollectionsPickerProps {
  open: boolean;
  onClose: () => void;
  onPick: (collection: SmartMatchCollectionRef) => void;
  /** Positioning hint — defaults to above the trigger. */
  anchor?: 'above' | 'below';
}

export function CollectionsPicker({
  open,
  onClose,
  onPick,
  anchor = 'above',
}: CollectionsPickerProps) {
  const [list, setList] = useState<SmartMatchCollection[]>([]);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    setList(readCollections());
    setCreating(false);
    setNewName('');
    const refresh = () => setList(readCollections());
    window.addEventListener(COLLECTIONS_CHANGE_EVENT, refresh);
    window.addEventListener('storage', refresh);
    return () => {
      window.removeEventListener(COLLECTIONS_CHANGE_EVENT, refresh);
      window.removeEventListener('storage', refresh);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [open, onClose]);

  const handleCreate = () => {
    const trimmed = newName.trim();
    if (!trimmed) return;
    const col = createCollection(trimmed);
    setCreating(false);
    setNewName('');
    onPick({ id: col.id, name: col.name });
  };

  const positionStyle = useMemo<React.CSSProperties>(
    () =>
      anchor === 'above'
        ? { bottom: 'calc(100% + 6px)', right: 0 }
        : { top: 'calc(100% + 6px)', right: 0 },
    [anchor],
  );

  const sortedList = useMemo(
    () => [...list].sort((a, b) => b.updatedAt - a.updatedAt),
    [list],
  );

  if (!open) return null;

  return (
    <div
      ref={rootRef}
      role="menu"
      aria-label="Pick a collection"
      className="absolute z-[60] w-[260px] rounded-[10px] border shadow-lg"
      style={{
        ...positionStyle,
        background: 'var(--vscode-bg-elevated)',
        borderColor: 'var(--vscode-border-primary)',
      }}
    >
      <div className="px-3 py-2">
        <p
          className="text-[10px] font-semibold uppercase tracking-wider"
          style={{ color: 'var(--vscode-text-tertiary)' }}
        >
          Save to collection
        </p>
      </div>

      {creating ? (
        <div
          className="flex items-center gap-2 border-t px-3 py-2"
          style={{ borderColor: 'var(--vscode-border-primary)' }}
        >
          <input
            autoFocus
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleCreate();
              if (e.key === 'Escape') setCreating(false);
            }}
            placeholder="Collection name"
            className="h-8 flex-1 rounded-[8px] border bg-[var(--vscode-bg-primary)] px-2 text-[12px] outline-none"
            style={{
              borderColor: 'var(--ggj-accent,#10B981)',
              color: 'var(--vscode-text-primary)',
            }}
          />
          <button
            type="button"
            onClick={handleCreate}
            disabled={!newName.trim()}
            className="inline-flex h-8 items-center rounded-[8px] bg-[var(--ggj-accent,#10B981)] px-3 text-[12px] font-medium text-white disabled:cursor-not-allowed disabled:opacity-50"
          >
            Create
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setCreating(true)}
          className="flex w-full items-center gap-2 border-t px-3 py-2 text-left text-[12px] transition-colors hover:bg-[var(--vscode-bg-hover)]"
          style={{
            borderColor: 'var(--vscode-border-primary)',
            color: 'var(--ggj-accent,#10B981)',
          }}
        >
          <Plus className="h-3.5 w-3.5" aria-hidden />
          <span className="font-medium">Create new</span>
        </button>
      )}

      <div
        className="max-h-[200px] overflow-y-auto border-t"
        style={{ borderColor: 'var(--vscode-border-primary)' }}
      >
        {sortedList.length === 0 ? (
          <p
            className="px-3 py-3 text-center text-[11px]"
            style={{ color: 'var(--vscode-text-tertiary)' }}
          >
            No collections yet. Create one above.
          </p>
        ) : (
          sortedList.map((c) => (
            <button
              key={c.id}
              type="button"
              role="menuitem"
              onClick={() => onPick({ id: c.id, name: c.name })}
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-[12px] transition-colors hover:bg-[var(--vscode-bg-hover)]"
              style={{ color: 'var(--vscode-text-primary)' }}
            >
              <FolderOpen
                className="h-3.5 w-3.5"
                style={{ color: 'var(--vscode-text-tertiary)' }}
                aria-hidden
              />
              <span className="min-w-0 flex-1 truncate">{c.name}</span>
            </button>
          ))
        )}
      </div>
    </div>
  );
}
